import type { ClientDriver } from "@/lib/api/drivers";

/** Upstream stores the two halves separately; either one may be blank. */
export function formatDriverName(driver: ClientDriver): string {
  return `${driver.firstName} ${driver.lastName}`.trim();
}

/**
 * One extra `id:` tag costs exactly three characters off the end of a stored driver reference (see
 * `parseDriverRef`), so the fragments a truncated reference can leave behind are the id sliced in
 * three-character steps. Below the floor a fragment is too short to name anybody with confidence.
 */
const FRAGMENT_STEP = 3;
const MIN_FRAGMENT_LENGTH = 8;

/** The truncation fragments of `id`, longest first; the id itself is not among them. */
function* driverIdFragments(id: string): Generator<string> {
  for (let length = id.length - FRAGMENT_STEP; length >= MIN_FRAGMENT_LENGTH; length -= FRAGMENT_STEP) {
    yield id.slice(0, length);
  }
}

/**
 * `fragment → whole driver id`, for recovering references that upstream truncated. A fragment two
 * drivers could both claim is dropped rather than guessed at, so an ambiguous reference stays
 * unresolved and keeps reading as a dangling assignment. Whole ids are registered last: a real id
 * always outranks a fragment of somebody else's.
 */
export function buildDriverIdFragmentMap(drivers: ClientDriver[]): Map<string, string> {
  const map = new Map<string, string>();
  const ambiguous = new Set<string>();

  for (const driver of drivers) {
    for (const fragment of driverIdFragments(driver.id)) {
      const claimed = map.get(fragment);
      if (claimed === undefined) map.set(fragment, driver.id);
      else if (claimed !== driver.id) ambiguous.add(fragment);
    }
  }

  for (const fragment of ambiguous) map.delete(fragment);
  for (const driver of drivers) map.set(driver.id, driver.id);

  return map;
}

/**
 * Vehicles carry only `driverId`, so the name is joined at the consumer. Live Tracking rewrites
 * its `vehicles` array every WS flush — resolving through a map keeps the vehicle objects
 * themselves untouched, so the memoized list and map children are not defeated.
 *
 * Truncation fragments are keyed too, so every renderer going through `resolveRefDisplay` shows the
 * driver's name for a mangled reference without having to know the reference was mangled.
 */
export function buildDriverNameMap(drivers: ClientDriver[]): Map<string, string> {
  const nameById = new Map<string, string>();
  for (const driver of drivers) {
    const name = formatDriverName(driver);
    if (name) nameById.set(driver.id, name);
  }

  const map = new Map<string, string>();
  for (const [fragment, id] of buildDriverIdFragmentMap(drivers)) {
    const name = nameById.get(id);
    if (name) map.set(fragment, name);
  }
  return map;
}
